
/**
 * 
 * @param date Date
 * @returns a string with formatted date GG-MM-AAAA hh-mm-ss
 */
export function formatDateAndTime(date: Date | string): string {
  const d = new Date(date);
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

function pad(n: number): string {
  return n < 10 ? '0' + n : n.toString();
}

/**
 * 
 * @param date 
 * @returns a string representing a date with format YYYY-MM-DD
 */
export function fromDateToString(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0); // clear time (optional since you're just formatting)
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
}

/**
 * 
 * @param dateStr 
 * @returns a Date object created from a string with format YYYY-MM-DD
 */
export function fromStringToDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);
  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Converts all Date properties of an object to string format (YYYY-MM-DD).
 * @param obj The object to process (e.g., an Activity).
 * @returns A new object with Date properties converted to strings.
 */
export function convertDatesToStrings<T>(obj: T): T {
  const result: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // Check if the property is a Date and convert it to a string
      if (value instanceof Date) {
        result[key] = fromDateToString(value);
      } else if (typeof value === 'object' && value !== null) {
        // Recursively handle nested objects
        result[key] = convertDatesToStrings(value);
      } else {
        result[key] = value;
      }
    }
  }

  return result;
}



/**
 * 
 * @param date Date
 * @returns a Date with time converted do midnightUTC
 */
export function toMiddayUTCDate(date: Date | undefined | null): Date | null {
  if (!date) return null;

  const d = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(d.getTime())) return null; // Invalid date check

  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0));
}

/**
 * This is a specific function that allows to handle dates both string (YYYY-MM-DD) and Date.
 * @param date 
 * @returns the relative Date object without time
 */
export function dateWithoutTime(date: Date | string | undefined | null): Date | null {

  if (!date) return null;

  if (date instanceof Date) {
    date.setHours(0, 0, 0, 0);
    return date;
  } else {
    // in case date object is a string YYYY-MM-DD
    const convertedDate = fromStringToDate(date);
    convertedDate?.setHours(0, 0, 0, 0);
    return convertedDate;
  }
}

/**
 * 
 * @param d1 Date, d2 Date
 * @returns true if the 2 dates are equal
 */
export function areDatesEqual(d1: Date | undefined | null, d2: Date | undefined | null): boolean {
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Converts a time string in "HH:MM" format to a number representing the time in minutes.
 * This function assumes the time string is in "HH:MM" format
 */
export function getMinutesFromTimeString(timeStr: string | null): number {
  if (!timeStr) return 0; // hande null or empty string

  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error('Invalid time string format');
  }
  return hours * 60 + minutes;
}

/**
 * Adds the given number of minutes to a time string in "HH:MM" format and returns the new time string.
 * Supports wrap-around at 24h and negative minute offsets.
 * @param timeStr The original time string in "HH:MM" format.
 * @param minutesToAdd The number of minutes to add (can be negative).
 * @returns The new time string in "HH:MM" format.
 */
export function addMinutesToTimeString(timeStr: string | null, minutesToAdd: number): string | null {
  if (!timeStr) return null;

  const [hoursStr, minutesStr] = timeStr.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  const isValidTime =
    Number.isInteger(hours) &&
    Number.isInteger(minutes) &&
    hours >= 0 &&
    hours <= 23 &&
    minutes >= 0 &&
    minutes <= 59;

  if (!isValidTime) {
    throw new Error('Invalid time string format');
  }
  if (!Number.isFinite(minutesToAdd)) {
    throw new Error('Invalid minutesToAdd value');
  }

  const total = hours * 60 + minutes;
  // normalize within [0, 1440)
  const normalized = ((total + Math.trunc(minutesToAdd)) % 1440 + 1440) % 1440;
  const newHours = Math.floor(normalized / 60);
  const newMinutes = normalized % 60;

  return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
}


/**
 * If the given date's day is > 20, return the 20th of the same month/year.
 * Otherwise return the date itself.
 */
export function clampTo20thOfMonth(dateInput: Date | string): Date {
  const d = dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (isNaN(d.getTime())) throw new Error('Invalid date');
  if (d.getDate() > 20) {
    d.setDate(20);
  }
  return d;
}
/**
 * Return the difference in days between two dates. If one of date is null or
 * dates are the same, returns 0.
 * The difference is calculated in a way that avoids DST issues by using UTC time. 
 *
 * 
 * @param date1 
 * @param date2 
 * @returns the number of days of difference
 */
export function calculateDifferenceInDays(date1: Date, date2: Date): number {
  if (!date1 || !date2) return 0;
  if (date1.getTime() === date2.getTime()) return 0;

  const baseDate = date1 < date2 ? date1 : date2;
  const currentDate = date1 > date2 ? date1 : date2;
  // Use UTC to avoid DST issues
  const baseDateUTC = Date.UTC(date1.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  const currentDateUTC = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  const diffInMillis = currentDateUTC - baseDateUTC;
  const daysOfDifference = Math.floor(diffInMillis / (1000 * 60 * 60 * 24));
  return daysOfDifference;
}

/**
 * Default locale used whenever the UI language is not available yet.
 * It matches the default language of the shared store ('it').
 */
export const DEFAULT_DATE_LOCALE = 'it';

/**
 * Returns the name of the week day of the given date, in the given language.
 * The name is extended and capitalized (Intl returns 'lunedì' in italian).
 *
 * @param date a Date, a YYYY-MM-DD string or a full ISO string
 * @param locale a BCP47 tag, i.e. the languageFilename of the selected language ('it' / 'en')
 * @returns the week day name, or null when the date is missing or invalid
 */
export function getWeekDayName(date: Date | string | undefined | null, locale = DEFAULT_DATE_LOCALE): string | null {
  if (!date) return null;

  // fromStringToDate handles the YYYY-MM-DD format used across the app without UTC shifts;
  // the Date fallback covers full ISO strings (TripDay.date can be either)
  const d = date instanceof Date ? date : (fromStringToDate(date) ?? new Date(date));
  if (isNaN(d.getTime())) return null;

  const weekDay = d.toLocaleDateString(locale || DEFAULT_DATE_LOCALE, { weekday: 'long' });
  return weekDay.charAt(0).toUpperCase() + weekDay.slice(1);
}
